import { ApolloError } from "@apollo/client";
import { BookingDataForSale, ClientInput, ExtraOption, ProductExtraOption, ProductForSale } from "../../types";
import { PaymentType } from "@me-team/host/main/graphql/types";
import { ChosenProductType } from "../../../modals/modalsUtils";

type SearchClientFn = (
    search: string,
    originalInput: { phone?: string; email?: string }
) => Promise<ClientInput | null>;

export const createDebouncedClientSearch = (
    findClient: SearchClientFn,
    onError: (error: ApolloError) => void,
    onLoading?: (loading: boolean) => void
) => {
    return debounce(
        async (
            search: string,
            originalInput: { phone?: string; email?: string },
            callback: (client: ClientInput | null) => void
        ) => {
            try {
                onLoading?.(true);
                const client = await findClient(search, originalInput);
                callback(client);
            } catch (error) {
                if (error instanceof ApolloError) {
                    onError(error);
                    callback(null);
                }
            } finally {
                onLoading?.(false);
            }
        },
        500
    );
};

export const filterOptionSaleProducts = (
    option: {
        label: string;
        value: number;
        data: ExtraOption<ProductExtraOption>;
    },
    inputValue: string
) => {
    const search = inputValue.trim().toLowerCase();
    return option.label.toLowerCase().includes(search) || option.data?.sku?.toLowerCase().includes(search);
};

export const getProductFieldName = (index: number, field: string) => `productsInput.${index}.${field}`;

export const calculateTotalAmount = (productsInput: ProductForSale[]) => {
    return productsInput.reduce((sum, product) => {
        const amount = Number(product.amount) || 0;
        return sum + amount;
    }, 0);
};

export const getFilteredProductOptions = (
    index: number,
    values: { productsInput: ProductForSale[] },
    allOptions: ExtraOption<ProductExtraOption>[]
): ExtraOption<ProductExtraOption>[] => {
    const selectedProductId = values.productsInput?.[index]?.product;

    const currentOption: ExtraOption<ProductExtraOption> = allOptions.find(
        (option) => option.value === selectedProductId
    );

    const selectedIdsInOtherRows = values.productsInput.map((item, i) => i !== index && item.product).filter(Boolean);

    const filtered = allOptions.filter((option) => !selectedIdsInOtherRows.includes(Number(option.value)));

    return currentOption
        ? [currentOption, ...filtered.filter((option) => option.value !== currentOption.value)]
        : filtered;
};

export function debounce<T extends (...args: any[]) => void>(func: T, delay: number): T & { cancel: () => void } {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const debouncedFunction = (...args: Parameters<T>) => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            func(...args);
        }, delay);
    };

    debouncedFunction.cancel = () => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };

    return debouncedFunction as T & { cancel: () => void };
}

export const getInitialEmployeeId = (
    action: "create" | "update",
    bookingUuid?: string,
    bookingDataForSale?: BookingDataForSale,
    chosenProduct?: { employee?: { id: number } },
    currentEmployeeId?: number
): number => {
    const sources: Record<string, () => number | undefined> = {
        "create:false": () => currentEmployeeId,
        "create:true": () => bookingDataForSale?.employee,
        "update:false": () => chosenProduct?.employee?.id,
        "update:true": () => bookingDataForSale?.employee,
    };

    return sources[`${action}:${Boolean(bookingUuid)}`]?.();
};

export const getInitialClient = (
    action: "create" | "update",
    bookingUuid?: string,
    bookingDataForSale?: BookingDataForSale,
    chosenProduct?: ChosenProductType
) => {
    const sources: Record<string, () => { name: string | null; phone: string | null; email: string | null }> = {
        "create:false": () => ({ name: null, phone: null, email: null }),
        "create:true": () => ({
            name: bookingDataForSale?.clientName || null,
            phone: bookingDataForSale?.clientPhone || null,
            email: bookingDataForSale?.clientEmail || null,
        }),
        "update:false": () => ({
            name: chosenProduct?.client?.name || null,
            phone: chosenProduct?.client?.phone || null,
            email: chosenProduct?.client?.email || null,
        }),
        "update:true": () => ({
            name: bookingDataForSale?.clientName || null,
            phone: bookingDataForSale?.clientPhone || null,
            email: bookingDataForSale?.clientEmail || null,
        }),
    };

    return sources[`${action}:${Boolean(bookingUuid)}`]();
};

export const mapPaymentType = (value?: string | null): PaymentType | null => {
    if (!value) return null;
    if (value === "Card" || value === "CARD") return PaymentType.Card;
    if (value === "Cash" || value === "CASH") return PaymentType.Cash;
    return null;
};

export const toNumberOrNull = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const str = String(value).trim();
    if (str === "") return null;
    const number = Number(str);
    return Number.isFinite(number) ? number : null;
};

export const roundTo2Safe = (n?: number | null) => {
    if (n === null || n === undefined) return null;
    const rounded = Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    return rounded === 0 ? 0.01 : rounded;
};
